<template>
  <div v-if="isOpen" class="modal-overlay" @click.self="closeModal">
    <div class="modal-container">
      <!-- Modal Header -->
      <div class="modal-header">
        <h2 class="modal-title">{{ isEditMode ? 'Edit Account' : 'Add Account' }}</h2>
        <button class="close-button" @click="closeModal" :disabled="loading">&#x2715;</button>
      </div>

      <!-- Modal Body -->
      <div class="modal-body">
        <form @submit.prevent="handleSubmit">
          <!-- Account Name -->
          <div class="form-group">
            <label for="accountName" class="form-label">Account Name <span class="required">*</span></label>
            <input
              id="accountName"
              v-model="formData.accountName"
              type="text"
              class="form-input"
              placeholder="e.g., Petty Cash, Main Operating Account"
              required
              :disabled="loading"
            />
          </div>

          <!-- Account Type -->
          <div class="form-group">
            <label for="accountType" class="form-label">Account Type <span class="required">*</span></label>
            <select
              id="accountType"
              v-model="formData.accountType"
              class="form-select"
              required
              :disabled="loading"
            >
              <option value="">Select Type</option>
              <option value="Cash">Cash</option>
              <option value="Bank">Bank</option>
            </select>
          </div>

          <!-- Bank Name -->
          <div class="form-group" v-if="formData.accountType === 'Bank'">
            <label for="bankName" class="form-label">Bank Name</label>
            <input
              id="bankName"
              v-model="formData.bankName"
              type="text"
              class="form-input"
              placeholder="e.g., First National Bank"
              :disabled="loading"
            />
          </div>

          <!-- Branch -->
          <div class="form-group" v-if="formData.accountType === 'Bank'">
            <label for="branch" class="form-label">Branch</label>
            <input
              id="branch"
              v-model="formData.branch"
              type="text"
              class="form-input"
              placeholder="e.g., Main Branch"
              :disabled="loading"
            />
          </div>

          <!-- Account Number -->
          <div class="form-group" v-if="formData.accountType === 'Bank'">
            <label for="accountNumber" class="form-label">Account Number</label>
            <input
              id="accountNumber"
              v-model="formData.accountNumber"
              type="text"
              class="form-input"
              placeholder="e.g., 1234567890"
              :disabled="loading"
            />
          </div>

          <!-- Opening Balance -->
          <div class="form-group">
            <label for="openingBalance" class="form-label">Opening Balance</label>
            <input
              id="openingBalance"
              v-model.number="formData.openingBalance"
              type="number"
              class="form-input"
              placeholder="0"
              :disabled="loading"
            />
          </div>
        </form>
      </div>

      <!-- Modal Footer -->
      <div class="modal-footer">
        <button class="btn-cancel" @click="closeModal" :disabled="loading">Cancel</button>
        <button class="btn-submit" @click="handleSubmit" :disabled="loading">
          <span v-if="loading" class="btn-spinner"></span>
          {{ submitButtonText }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'AddAccountModal',
  props: {
    isOpen: {
      type: Boolean,
      default: false
    },
    account: {
      type: Object,
      default: null
    },
    loading: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      formData: {
        accountName: '',
        accountType: 'Bank',
        bankName: '',
        branch: '',
        accountNumber: '',
        openingBalance: 0
      }
    }
  },
  computed: {
    isEditMode() {
      return !!this.account
    },
    submitButtonText() {
      if (this.loading) {
        return this.isEditMode ? 'Updating...' : 'Adding...'
      }
      return this.isEditMode ? 'Update Account' : 'Add Account'
    }
  },
  watch: {
    account: {
      immediate: true,
      handler(newAccount) {
        if (newAccount) {
          this.formData = {
            accountName: newAccount.accountName || newAccount.name || '',
            accountType: newAccount.accountType || newAccount.type || 'Bank',
            bankName: newAccount.bankName || newAccount.bankInfo?.name || '',
            branch: newAccount.branch || '',
            accountNumber: newAccount.accountNumber || newAccount.bankInfo?.number || '',
            openingBalance: newAccount.openingBalance || newAccount.balance || 0
          }
        } else {
          this.resetForm()
        }
      }
    },
    isOpen(newVal) {
      if (!newVal) {
        this.resetForm()
      }
    }
  },
  methods: {
    closeModal() {
      if (this.loading) return
      this.$emit('close')
    },
    handleSubmit() {
      if (this.loading) return

      if (!this.formData.accountName || !this.formData.accountType) {
        alert('Please fill in all required fields')
        return
      }

      if (this.isEditMode) {
        this.$emit('update', { ...this.formData })
      } else {
        this.$emit('create', { ...this.formData })
      }
    },
    resetForm() {
      this.formData = {
        accountName: '',
        accountType: 'Bank',
        bankName: '',
        branch: '',
        accountNumber: '',
        openingBalance: 0
      }
    }
  }
}
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-container {
  background: white;
  border-radius: 12px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
  width: 90%;
  max-width: 500px;
  max-height: 90vh;
  overflow-y: auto;
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 24px;
  border-bottom: 1px solid #e5e7eb;
  position: sticky;
  top: 0;
  background: white;
}

.modal-title {
  font-size: 20px;
  font-weight: 700;
  color: #1f2937;
  margin: 0;
}

.close-button {
  background: none;
  border: none;
  font-size: 24px;
  color: #6b7280;
  cursor: pointer;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  transition: background 0.2s ease;
}

.close-button:hover:not(:disabled) {
  background: #f3f4f6;
  color: #1f2937;
}

.close-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.modal-body {
  padding: 24px;
}

.form-group {
  margin-bottom: 20px;
}

.form-label {
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 8px;
}

.required {
  color: #ef4444;
}

.form-input,
.form-select {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 14px;
  color: #1f2937;
  transition: border-color 0.2s ease;
  font-family: inherit;
}

.form-input:focus,
.form-select:focus {
  outline: none;
  border-color: #2563eb;
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
}

.form-input:disabled,
.form-select:disabled {
  background: #f9fafb;
  cursor: not-allowed;
}

.form-input::placeholder {
  color: #9ca3af;
}

.form-select {
  cursor: pointer;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3E%3Cpath stroke='%236B7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3E%3C/svg%3E");
  background-position: right 8px center;
  background-repeat: no-repeat;
  background-size: 20px;
  padding-right: 32px;
}

.modal-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  padding: 24px;
  border-top: 1px solid #e5e7eb;
  background: #f9fafb;
}

.btn-cancel {
  padding: 10px 24px;
  border: 1px solid #e5e7eb;
  background: white;
  color: #1f2937;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-cancel:hover:not(:disabled) {
  background: #f3f4f6;
  border-color: #d1d5db;
}

.btn-cancel:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-submit {
  padding: 10px 24px;
  background: #2563eb;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s ease;
  display: flex;
  align-items: center;
  gap: 8px;
}

.btn-submit:hover:not(:disabled) {
  background: #1d4ed8;
}

.btn-submit:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.btn-spinner {
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: white;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

@media (max-width: 640px) {
  .modal-container {
    width: 95%;
  }

  .modal-header {
    padding: 16px;
  }

  .modal-body {
    padding: 16px;
  }

  .modal-footer {
    padding: 16px;
    flex-direction: column-reverse;
  }

  .btn-cancel,
  .btn-submit {
    width: 100%;
    justify-content: center;
  }
}
</style>
